'use client';

/**
 * @module
 * @category Component
 */

import 'BomdisoftComponents/inputs/phone/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { Select } from 'BomdisoftComponents/inputs/field/select/index';
import { DEFAULT_PHONE_COUNTRY, joinPhone, PHONE_COUNTRY_OPTIONS, splitPhone } from 'BomdisoftComponents/inputs/phone/country';
import { countryCode, getLanguageFromLocale, phoneT } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ChangeEvent, type KeyboardEvent, useId, useState } from 'react';

export type PhoneInputProps = {
  className?: string;
  country?: string;
  error?: boolean;
  helperText?: string;
  label?: string;
  onChange?: (value: string) => void;
  onKeyUp?: (event: KeyboardEvent) => void;
  required?: boolean;
  value?: string;
};

export const PhoneInput = ({ className, country = DEFAULT_PHONE_COUNTRY, error, helperText, label, onChange, onKeyUp, required, value }: PhoneInputProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const id = useId();
  const [ownValue, setOwnValue] = useState('');
  const [chosen, setChosen] = useState(country);
  const parts = splitPhone(value ?? ownValue, chosen);

  const commit = (next: string) => {
    if (value === undefined) setOwnValue(next);
    onChange?.(next);
  };

  const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setChosen(event.target.value);
    commit(joinPhone(event.target.value, parts.number));
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => commit(joinPhone(parts.country, event.target.value));

  return (
    <div className={joinClasses('bomdisoft-phone-input', className)}>
      <fieldset aria-labelledby={`${id}-label`} className='bomdisoft-phone-input-group' data-error={error ? '' : undefined}>
        <label className='bomdisoft-phone-input-label' data-error={error ? '' : undefined} data-required={required ? '' : undefined} htmlFor={`${id}-number`} id={`${id}-label`}>
          {label || phoneT[language]}
        </label>
        <Select aria-label={countryCode[language]} className='bomdisoft-phone-input-extension-wrap' onChange={handleCountryChange} size='small' value={parts.country} variant='bare'>
          {PHONE_COUNTRY_OPTIONS.map(option => (
            <option data-trigger-label={option.triggerLabel} key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <input
          aria-describedby={helperText ? `${id}-helper` : undefined}
          aria-invalid={error ? true : undefined}
          className='bomdisoft-phone-input-number'
          id={`${id}-number`}
          inputMode='numeric'
          onChange={handleNumberChange}
          onKeyUp={onKeyUp}
          pattern='[0-9]*'
          required={required}
          type='tel'
          value={parts.number}
        />
      </fieldset>
      {helperText ? (
        <p className='bomdisoft-phone-input-helper' data-error={error ? '' : undefined} id={`${id}-helper`}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
};
